<script setup>
/**
 * Keyboard Shortcuts Modal
 * UX/UI Sprint 2 - Task 2.4
 *
 * Displays all available keyboard shortcuts for the Pipeline board
 * Design inspired by Linear/Notion style
 */

import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { PIPELINE_SHORTCUTS } from 'dashboard/composables/usePipelineKeyboard';

defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

const formatAccessibleKeySequence = keys => keys.join(' + ');

// Translate shortcut categories
const translatedShortcuts = computed(() => {
  return PIPELINE_SHORTCUTS.map(category => ({
    ...category,
    title: t(`PIPELINE.KEYBOARD_SHORTCUTS.CATEGORIES.${category.category}`),
    items: category.items.map(item => ({
      ...item,
      label: t(`PIPELINE.KEYBOARD_SHORTCUTS.${item.description}`),
      accessibleKeySequence: formatAccessibleKeySequence(item.keys),
    })),
  }));
});

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <Modal
    :show="show"
    size="medium"
    initial-focus-selector="#keyboard-shortcuts-close"
    @close="handleClose"
  >
    <section
      class="flex max-h-[min(88vh,48rem)] flex-col overflow-hidden"
      data-modal="keyboard-shortcuts"
    >
      <!-- Header -->
      <header class="border-b border-n-weak px-4 py-4 sm:px-6 sm:py-5">
        <div class="flex items-center gap-3 pr-10">
          <div
            class="flex size-12 shrink-0 items-center justify-center rounded-xl bg-n-brand text-white"
            aria-hidden="true"
          >
            <fluent-icon icon="keyboard" size="20" />
          </div>
          <div>
            <h2 class="text-heading-2 text-n-slate-12">
              {{ t('PIPELINE.KEYBOARD_SHORTCUTS.TITLE') }}
            </h2>
            <p class="text-label-small text-n-slate-11 mt-1">
              {{ t('PIPELINE.KEYBOARD_SHORTCUTS.DESCRIPTION') }}
            </p>
          </div>
        </div>
      </header>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
        <section
          v-for="category in translatedShortcuts"
          :key="category.category"
          class="mb-6 last:mb-0"
        >
          <h3
            class="text-label-small font-semibold uppercase tracking-wider text-n-slate-11 mb-3"
          >
            {{ category.title }}
          </h3>

          <ul class="flex flex-col gap-2">
            <li
              v-for="(item, index) in category.items"
              :key="index"
              class="flex min-h-12 flex-col gap-2 rounded-lg bg-n-alpha-1 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <span class="text-body-main text-n-slate-11">{{
                item.label
              }}</span>
              <div class="flex flex-wrap gap-1">
                <span class="sr-only" data-test="keyboard-shortcut-sequence">
                  {{ item.accessibleKeySequence }}
                </span>
                <kbd
                  v-for="(key, keyIndex) in item.keys"
                  :key="keyIndex"
                  class="bg-n-alpha-2 rounded-md px-2 py-0.5 text-label-small text-n-slate-12 font-mono border border-n-weak shadow-sm"
                >
                  <span aria-hidden="true">{{ key }}</span>
                </kbd>
              </div>
            </li>
          </ul>
        </section>
      </div>

      <!-- Footer -->
      <footer
        class="flex flex-col gap-3 rounded-b-2xl border-t border-n-weak bg-n-alpha-1 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6"
      >
        <p class="text-center text-label-small text-n-slate-11 sm:text-left">
          {{ t('PIPELINE.KEYBOARD_SHORTCUTS.HINT') }}
        </p>
        <Button
          id="keyboard-shortcuts-close"
          variant="ghost"
          color="slate"
          size="lg"
          :label="t('PIPELINE.CLOSE_MODAL')"
          class="w-full sm:w-auto"
          @click="handleClose"
        />
      </footer>
    </section>
  </Modal>
</template>
